import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogContent, DialogTitle,
  FormControl, Grid, InputLabel, MenuItem, Select, Stack, TextField,
  Typography, IconButton, Divider, Alert,
} from '@mui/material';
import { Close, DirectionsCar, AttachMoney, Cancel, CheckCircle, ExpandMore } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import DataTableCard from '../../components/ui/DataTableCard';
import EnhancedDataGrid from '../../components/ui/EnhancedDataGrid';
import api from '../../services/api';
import { formatMoney } from '../../utils/currency';

const STATUS_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  pending: 'warning',
  searching: 'info',
  accepted: 'primary',
  arrived: 'info',
  started: 'secondary',
  completed: 'success',
  cancelled: 'error',
};

interface StatCardProps {
  label: string;
  value: number | string;
  color?: string;
  icon?: React.ReactNode;
}

function StatCard({ label, value, color, icon }: StatCardProps) {
  return (
    <Card elevation={0} sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider', height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {icon && (
            <Box sx={{ color: color || 'primary.main', display: 'flex' }}>
              {icon}
            </Box>
          )}
          <Box>
            <Typography variant="h5" fontWeight={800} color={color || 'text.primary'}>
              {value}
            </Typography>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

interface Ride {
  _id: string;
  rideNumber?: string;
  status?: string;
  tripType?: string;
  scheduleMode?: string;
  fare?: { total?: number };
  createdAt?: string;
  customerId?: { firstName?: string; lastName?: string };
  driverId?: { firstName?: string; lastName?: string };
  pickup?: { address?: string };
  dropoff?: { address?: string };
  paymentMethod?: string;
  rideOtp?: string;
}

export default function RidesDashboardPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [tripTypeFilter, setTripTypeFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [detailRide, setDetailRide] = useState<Ride | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['rides-dashboard', statusFilter, tripTypeFilter, fromDate, toDate],
    queryFn: async () => {
      const res = await api.get('/rides', {
        params: {
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(tripTypeFilter ? { type: tripTypeFilter } : {}),
          ...(fromDate ? { fromDate } : {}),
          ...(toDate ? { toDate } : {}),
          limit: 200,
        },
      });
      return res.data;
    },
  });

  const rides: Ride[] = data?.data || [];

  const todayStr = new Date().toDateString();
  const todayRides = rides.filter((r) => r.createdAt && new Date(r.createdAt).toDateString() === todayStr);
  const activeRides = rides.filter((r) => ['searching', 'accepted', 'arrived', 'started'].includes(r.status || ''));
  const completedRides = rides.filter((r) => r.status === 'completed');
  const cancelledRides = rides.filter((r) => r.status === 'cancelled');
  const totalRevenue = completedRides.reduce((sum, r) => sum + (r.fare?.total || 0), 0);

  const columns: GridColDef[] = [
    { field: 'rideNumber', headerName: 'Ride #', width: 130 },
    {
      field: 'customerId', headerName: 'Customer', flex: 1, minWidth: 120,
      valueGetter: (_v, row) => row.customerId ? `${row.customerId.firstName || ''} ${row.customerId.lastName || ''}`.trim() : '—',
    },
    {
      field: 'driverId', headerName: 'Driver', flex: 1, minWidth: 120,
      valueGetter: (_v, row) => row.driverId ? `${row.driverId.firstName || ''} ${row.driverId.lastName || ''}`.trim() : 'Unassigned',
    },
    { field: 'tripType', headerName: 'Type', width: 100, valueGetter: (_v, row) => row.tripType || 'LOCAL' },
    {
      field: 'pickup', headerName: 'Pickup', flex: 1, minWidth: 120,
      valueGetter: (_v, row) => row.pickup?.address || '—',
    },
    {
      field: 'dropoff', headerName: 'Drop', flex: 1, minWidth: 120,
      valueGetter: (_v, row) => row.dropoff?.address || '—',
    },
    {
      field: 'status', headerName: 'Status', width: 110,
      renderCell: (params) => (
        <Chip label={params.value || '—'} color={STATUS_COLORS[params.value as string] || 'default'} size="small" />
      ),
    },
    {
      field: 'fare', headerName: 'Fare', width: 90,
      valueGetter: (_v, row) => `${formatMoney(row.fare?.total || 0, row)}`,
    },
    {
      field: 'createdAt', headerName: 'Date', width: 150,
      valueFormatter: (value) => value ? new Date(value as string).toLocaleString() : '—',
    },
    {
      field: '_detail', headerName: '', width: 60, sortable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={() => setDetailRide(params.row as Ride)}>
          <ExpandMore fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <PageShell>
      <PageHeader
        eyebrow="Transport"
        title="Rides Dashboard"
        subtitle="Monitor all rides with live status, filter by date range, status, and trip type."
        gradient
      />

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => refetch()}>Retry</Button>}>
          Failed to load rides. Check your connection and try again.
        </Alert>
      )}

      {/* Stats bar */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard label="Today's Rides" value={todayRides.length} icon={<DirectionsCar />} />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard label="Active" value={activeRides.length} color="primary.main" icon={<DirectionsCar />} />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard label="Completed" value={completedRides.length} color="success.main" icon={<CheckCircle />} />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard label="Cancelled" value={cancelledRides.length} color="error.main" icon={<Cancel />} />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard label="Revenue" value={`${formatMoney(totalRevenue)}`} color="warning.main" icon={<AttachMoney />} />
        </Grid>
      </Grid>

      {/* Filters */}
      <Box sx={{ mb: 2, p: 2, borderRadius: 2, border: 1, borderColor: 'divider' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            label="From Date"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          <TextField
            size="small"
            label="To Date"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {['searching', 'accepted', 'arrived', 'started', 'completed', 'cancelled'].map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Trip Type</InputLabel>
            <Select value={tripTypeFilter} label="Trip Type" onChange={(e) => setTripTypeFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {['LOCAL', 'RENTAL', 'OUTSTATION'].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="outlined"
            onClick={() => { setStatusFilter(''); setTripTypeFilter(''); setFromDate(''); setToDate(''); }}
          >
            Clear
          </Button>
        </Stack>
      </Box>

      <DataTableCard title="All Rides" subtitle={`${rides.length} ride(s) matching filters`}>
        <EnhancedDataGrid
          rows={rides}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row._id as string}
          autoHeight
          exportFileName="rides-dashboard"
          statusField="status"
          statusOptions={[
            { value: 'searching', label: 'Searching' },
            { value: 'accepted', label: 'Accepted' },
            { value: 'started', label: 'Started' },
            { value: 'completed', label: 'Completed' },
            { value: 'cancelled', label: 'Cancelled' },
          ]}
        />
      </DataTableCard>

      {/* Ride Detail Modal */}
      <Dialog open={!!detailRide} onClose={() => setDetailRide(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={700}>
              Ride #{detailRide?.rideNumber || '—'}
            </Typography>
            <IconButton size="small" onClick={() => setDetailRide(null)}><Close /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {detailRide && (
            <Stack spacing={2}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box mt={0.5}>
                    <Chip label={detailRide.status || '—'} color={STATUS_COLORS[detailRide.status || ''] || 'default'} size="small" />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Trip Type</Typography>
                  <Typography variant="body2" fontWeight={600}>{detailRide.tripType || 'LOCAL'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Schedule Mode</Typography>
                  <Typography variant="body2">{detailRide.scheduleMode || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">OTP</Typography>
                  <Typography variant="body2" fontWeight={700}>{detailRide.rideOtp || '—'}</Typography>
                </Grid>
              </Grid>
              <Divider />
              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Customer</Typography>
                <Typography variant="body2">
                  {detailRide.customerId ? `${detailRide.customerId.firstName || ''} ${detailRide.customerId.lastName || ''}`.trim() : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Driver</Typography>
                <Typography variant="body2">
                  {detailRide.driverId ? `${detailRide.driverId.firstName || ''} ${detailRide.driverId.lastName || ''}`.trim() : 'Unassigned'}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Pickup</Typography>
                <Typography variant="body2" color="text.secondary">{detailRide.pickup?.address || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Drop</Typography>
                <Typography variant="body2" color="text.secondary">{detailRide.dropoff?.address || '—'}</Typography>
              </Box>
              <Box
                sx={{ borderRadius: 2, bgcolor: 'action.hover', height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Typography variant="caption" color="text.secondary">Map placeholder — integrate with Google Maps / Leaflet</Typography>
              </Box>
              <Divider />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Total Fare</Typography>
                  <Typography variant="h6" fontWeight={800} color="success.main">{formatMoney(detailRide.fare?.total || 0, detailRide)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Payment Method</Typography>
                  <Typography variant="body2" fontWeight={600}>{detailRide.paymentMethod || '—'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Date</Typography>
                  <Typography variant="body2">
                    {detailRide.createdAt ? new Date(detailRide.createdAt).toLocaleString() : '—'}
                  </Typography>
                </Grid>
              </Grid>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
